import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Grid,
  GridItem,
  Heading,
  Text,
  Flex,
} from '@chakra-ui/react'
import React from 'react'
import { Doughnut } from 'react-chartjs-2'
import moment from 'moment'
import { Regimen } from '../../../../../types/Regimen'

interface DentalVisualizationsProps {
  isSaved: boolean
  loadingProcedures: boolean
  dentalProcedures: Regimen[]
  dentalItems: Record<string, string>
  amalgamChartData: any
  amalgamChartOptions: any
  rootCanalChartData: any
  rootCanalChartOptions: any
  implantChartData: any
  implantChartOptions: any
}

const DentalVisualizations: React.FC<DentalVisualizationsProps> = ({
  isSaved,
  loadingProcedures,
  dentalProcedures,
  dentalItems,
  amalgamChartData,
  amalgamChartOptions,
  rootCanalChartData,
  rootCanalChartOptions,
  implantChartData,
  implantChartOptions,
}) => {
  if (!isSaved) return null

  return (
    <>
      {/* Dental Procedures History Section */}
      <Text fontSize='lg' fontWeight='bold' mt={2}>
        Dental Procedures History
      </Text>
      {loadingProcedures ? (
        <Flex justify='center' py={4}>
          <Spinner />
        </Flex>
      ) : dentalProcedures.length === 0 ? (
        <Text color='gray.500' py={2}>
          No dental procedures recorded. You can add dental procedures in the
          Monthly Reports section.
        </Text>
      ) : (
        <TableContainer>
          <Table variant='simple' size='sm'>
            <Thead>
              <Tr>
                <Th>Procedure Date</Th>
                <Th>Dental Procedure</Th>
              </Tr>
            </Thead>
            <Tbody>
              {dentalProcedures.map((procedure) => {
                // Get the last (most recent) usage
                const lastUsage = procedure.usages[procedure.usages.length - 1]
                const procedureId = lastUsage?.fields?.dentalProcedureId

                return (
                  <Tr key={procedure.id}>
                    <Td>
                      {lastUsage?.startDate
                        ? moment(lastUsage.startDate).format('MM/DD/YYYY')
                        : 'Unknown date'}
                    </Td>
                    <Td>{dentalItems[procedureId] || 'Unknown procedure'}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Donut Charts for Dental Statistics */}
      <Box mt={8} mb={4}>
        <Heading size='md' mb={4}>
          Dental Statistics Visualizations
        </Heading>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
          {/* Amalgam Chart */}
          <GridItem>
            <Box
              p={4}
              bg='white'
              borderRadius='xl'
              boxShadow='md'
              borderWidth='1px'
              borderColor='gray.100'
              height='220px'
            >
              <Box height='200px' position='relative'>
                <Doughnut
                  data={amalgamChartData}
                  options={amalgamChartOptions}
                />
                {/* Add total count text in the center */}
                <Flex
                  position='absolute'
                  top='50%'
                  left='50%'
                  transform='translate(-50%, -50%)'
                  flexDirection='column'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Text fontWeight='bold' fontSize='xl'>
                    {amalgamChartData?.datasets?.[0]?.data?.reduce(
                      (a: number, b: number) => a + b,
                      0
                    ) || 0}
                  </Text>
                  <Text fontSize='xs'>Total</Text>
                </Flex>
              </Box>
            </Box>
          </GridItem>

          {/* Root Canal Chart */}
          <GridItem>
            <Box
              p={4}
              bg='white'
              borderRadius='xl'
              boxShadow='md'
              borderWidth='1px'
              borderColor='gray.100'
              height='220px'
            >
              <Box height='200px' position='relative'>
                <Doughnut
                  data={rootCanalChartData}
                  options={rootCanalChartOptions}
                />
                {/* Add total count text in the center */}
                <Flex
                  position='absolute'
                  top='50%'
                  left='50%'
                  transform='translate(-50%, -50%)'
                  flexDirection='column'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Text fontWeight='bold' fontSize='xl'>
                    {rootCanalChartData?.datasets?.[0]?.data?.reduce(
                      (a: number, b: number) => a + b,
                      0
                    ) || 0}
                  </Text>
                  <Text fontSize='xs'>Total</Text>
                </Flex>
              </Box>
            </Box>
          </GridItem>

          {/* Implant Chart */}
          <GridItem>
            <Box
              p={4}
              bg='white'
              borderRadius='xl'
              boxShadow='md'
              borderWidth='1px'
              borderColor='gray.100'
              height='220px'
            >
              <Box height='200px' position='relative'>
                <Doughnut
                  data={implantChartData}
                  options={implantChartOptions}
                />
                {/* Add total count text in the center */}
                <Flex
                  position='absolute'
                  top='50%'
                  left='50%'
                  transform='translate(-50%, -50%)'
                  flexDirection='column'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Text fontWeight='bold' fontSize='xl'>
                    {implantChartData?.datasets?.[0]?.data?.reduce(
                      (a: number, b: number) => a + b,
                      0
                    ) || 0}
                  </Text>
                  <Text fontSize='xs'>Total</Text>
                </Flex>
              </Box>
            </Box>
          </GridItem>
        </Grid>
      </Box>
    </>
  )
}

export default DentalVisualizations
